/**
 * sl-yphr.23: ELSTER Fullscreen View — Professional UStVA Form UI
 *
 * Scrollable form with sidebar navigation, ELSTER-like sections,
 * Kz badges, two-column tables, dashboard overview, and sealed history.
 *
 * Layout: Sidebar Nav (left) + Scroll Content (right) + Sticky Action Bar (bottom)
 *
 * Preserves all existing logic from sl-yphr.14/29/30:
 * - Bidirectional database cell editing
 * - Schema migration
 * - Sealed history with Kz-Snapshot detail
 * - Validate/Submit via ERiCA REST API
 */

import { notify } from '@affine/component';
import { Button } from '@affine/component/ui/button';
import { workspaceApiFetch } from '@affine/core/modules/cloud';
import {
  createDatabaseBlockDataSource,
  DatabaseBlockDataSource,
} from '@blocksuite/affine/blocks/database';
import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import {
  type FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useSignalValue } from '../../doc-info/utils';
import { EditorService } from '../../editor';
import { WorkspaceService } from '../../workspace';
import type { FullscreenViewProps } from '../services/fullscreen-registry';
import { FullscreenRegistry } from '../services/fullscreen-registry';
import * as styles from './elster-fullscreen-view.css';
import { executeSchemaMigration } from './schema-executor';
import {
  SchemaMigrationBanner as ElsterMigrationBanner,
  SchemaMigrationDialog as ElsterMigrationDialog,
} from './schema-migration-dialog';
import { useElsterSchemaMigration } from './use-elster-schema-migration';
import {
  useKzFieldValidation,
  useKzTooltips,
  useUstvaSchema,
  useUstvaSections,
  type UStVAField,
  type UStVASection,
} from './use-elster-schema-validation';
import { calculateZahllast, extractKzFromColumnName } from './ustva-constants';

// =============================================================================
// Types
// =============================================================================

interface ElsterBlock {
  id: string;
  title: string;
  inputTableId: string | null;
  inputTableName: string | null;
  selectedRowIndex: number | null;
  schemaVersion: string | null;
}

interface EricaWorkspaceConfig {
  configured: boolean;
  steuernummer?: string;
  bundesland?: string;
}

function elsterPolicy(workspaceId: string, key: string, foreground = false) {
  return {
    workspaceId,
    category: foreground
      ? ('foreground-action' as const)
      : ('workspace-api' as const),
    key: `elster:${key}`,
    mode: foreground ? ('foreground' as const) : ('background' as const),
    priority: foreground ? ('foreground' as const) : ('background' as const),
  };
}

type KzEntry = { value: number; propId: string; rowId: string };
const EMPTY_IDS: string[] = [];

// =============================================================================
// Hooks
// =============================================================================

/** Find all affine:elster blocks in the current doc */
function useElsterBlocks(): ElsterBlock[] {
  const editorService = useServiceOptional(EditorService);
  const editor = editorService?.editor;
  const editorContainer = useLiveData(editor?.editorContainer$ ?? null);

  return useMemo(() => {
    const doc = editorContainer?.doc;
    if (!doc?.root) return [];

    const blocks: ElsterBlock[] = [];
    for (const child of doc.root.children) {
      if (child.flavour === 'affine:elster') {
        const props = child.props as Record<string, unknown>;
        blocks.push({
          id: child.id,
          title: String(props.title ?? 'UStVA'),
          inputTableId: (props.inputTableId as string) ?? null,
          inputTableName: (props.inputTableName as string) ?? null,
          selectedRowIndex: (props.selectedRowIndex as number) ?? null,
          schemaVersion: (props.schemaVersion as string) ?? null,
        });
      }
    }
    return blocks;
  }, [editorContainer]);
}

/** Create DatabaseBlockDataSource for a database block */
function useDatabaseDataSource(
  databaseBlockId: string | null
): DatabaseBlockDataSource | null {
  const editorService = useServiceOptional(EditorService);
  const editor = editorService?.editor;
  const editorContainer = useLiveData(editor?.editorContainer$ ?? null);

  return useMemo(() => {
    if (!databaseBlockId || !editorContainer?.doc) return null;
    const block = editorContainer.doc.getBlock(databaseBlockId);
    if (!block?.model) return null;
    return createDatabaseBlockDataSource(block.model as DatabaseBlockModel);
  }, [databaseBlockId, editorContainer]);
}

/** Read row titles from database */
function useDatabaseRows(dataSource: DatabaseBlockDataSource | null): string[] {
  const rowCount = useSignalValue(dataSource?.rowCount$) ?? 0;
  return useMemo(() => {
    if (!dataSource) return [];
    const rowIds =
      rowCount > 0 ? dataSource.getBoundedRowsWindow().rowIds : EMPTY_IDS;
    return rowIds.map((rowId: string) => {
      const title = dataSource.cellValueGet(rowId, 'title');
      return title ? String(title) : '';
    });
  }, [dataSource, rowCount]);
}

/** Read Kz values + their property IDs for bidirectional editing */
function useKzData(
  dataSource: DatabaseBlockDataSource | null,
  rowIndex: number | null
): Map<number, KzEntry> {
  const rowCount = useSignalValue(dataSource?.rowCount$) ?? 0;
  const columnCount = useSignalValue(dataSource?.columnCount$) ?? 0;
  return useMemo(() => {
    const data = new Map<number, KzEntry>();
    if (!dataSource || rowIndex === null) return data;
    const rowIds =
      rowCount > 0 ? dataSource.getBoundedRowsWindow().rowIds : EMPTY_IDS;
    const propIds =
      columnCount > 0
        ? dataSource.getBoundedPropertyIdsWindow().propertyIds
        : EMPTY_IDS;

    if (rowIndex < 0 || rowIndex >= rowIds.length) return data;

    const rowId = rowIds[rowIndex];

    for (const propId of propIds) {
      const name = dataSource.propertyNameGet(propId);
      const kz = extractKzFromColumnName(name);
      if (kz === null) continue;

      const raw = dataSource.cellValueGet(rowId, propId);
      const value = typeof raw === 'number' && !isNaN(raw) ? raw : 0;
      data.set(kz, { value, propId, rowId });
    }

    return data;
  }, [dataSource, rowIndex, rowCount, columnCount]);
}

/** Fetch workspace ELSTER config */
function useEricaConfig(): EricaWorkspaceConfig | null {
  const workspaceId = useService(WorkspaceService).workspace.id;
  const [config, setConfig] = useState<EricaWorkspaceConfig | null>(null);

  useEffect(() => {
    let cancelled = false;
    workspaceApiFetch(
      `/api/workspace/${workspaceId}/erica-config`,
      {
        credentials: 'include',
      },
      elsterPolicy(workspaceId, 'config')
    )
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (!cancelled) setConfig(data ?? { configured: false });
      })
      .catch(() => {
        if (!cancelled) setConfig({ configured: false });
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  return config;
}

/** Track active section via Intersection Observer */
function useActiveSectionObserver(sectionRefs: {
  current: Map<number, HTMLElement | null>;
}): number | null {
  const [activeSection, setActiveSection] = useState<number | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        // Find the topmost visible section
        let topEntry: IntersectionObserverEntry | null = null;
        for (const entry of entries) {
          if (
            entry.isIntersecting &&
            (!topEntry ||
              entry.boundingClientRect.top < topEntry.boundingClientRect.top)
          ) {
            topEntry = entry;
          }
        }
        if (topEntry) {
          const num = parseInt(
            topEntry.target.getAttribute('data-section') ?? '0',
            10
          );
          if (num > 0) setActiveSection(num);
        }
      },
      { threshold: 0.1, rootMargin: '-80px 0px -60% 0px' }
    );

    for (const el of sectionRefs.current.values()) {
      if (el) observer.observe(el);
    }

    return () => observer.disconnect();
  }, [sectionRefs]);

  return activeSection;
}

// =============================================================================
// Helpers
// =============================================================================

function formatCurrency(value: number): string {
  return value.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatEuro(value: number): string {
  return Math.round(value).toLocaleString('de-DE');
}

// =============================================================================
// Sub-Components
// =============================================================================

/** Single Kz input field with badge, tooltip, and schema-driven validation */
const KzFieldInput: FC<{
  field: UStVAField;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  error?: string;
  /** Full XSD label shown as tooltip on hover */
  schemaTooltip?: string;
  /** Schema-based validation function */
  validateFn?: (
    kz: number,
    rawValue: string
  ) => { valid: boolean; error: string | null };
}> = ({
  field,
  value,
  onChange,
  disabled,
  error,
  schemaTooltip,
  validateFn,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isCheckbox = field.inputType === 'checkbox';
  const isReadonly = field.inputType === 'readonly' || disabled;
  const isDecimal = field.inputType === 'euro-cent';

  const [localValue, setLocalValue] = useState(
    value === 0 ? '' : isDecimal ? formatCurrency(value) : formatEuro(value)
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  // Sync external changes
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setLocalValue(
        value === 0 ? '' : isDecimal ? formatCurrency(value) : formatEuro(value)
      );
    }
  }, [value, isDecimal]);

  const handleChange = useCallback(
    (rawValue: string) => {
      setLocalValue(rawValue);
      // Validate on each keystroke
      if (validateFn) {
        const result = validateFn(field.kz, rawValue);
        setValidationError(result.error);
      }
    },
    [validateFn, field.kz]
  );

  const handleBlur = useCallback(() => {
    // Remove thousands separators, replace comma with dot
    const cleaned = localValue.replace(/\./g, '').replace(',', '.');
    const parsed = parseFloat(cleaned);
    const num = isNaN(parsed)
      ? 0
      : isDecimal
        ? Math.round(parsed * 100) / 100
        : Math.round(parsed);
    onChange(num);
    setLocalValue(
      num === 0 ? '' : isDecimal ? formatCurrency(num) : formatEuro(num)
    );
    // Clear validation error on successful commit
    if (num === 0 && localValue.trim() === '') {
      setValidationError(null);
    }
  }, [localValue, onChange, isDecimal]);

  const displayError = error || validationError;

  if (isCheckbox) {
    return (
      <div className={styles.kzInputWrapper} title={schemaTooltip}>
        <input
          type="checkbox"
          className={styles.kzCheckbox}
          checked={value !== 0}
          onChange={e => onChange(e.target.checked ? 1 : 0)}
          disabled={disabled}
        />
        <span className={styles.kzBadge}>Kz{field.kz}</span>
      </div>
    );
  }

  return (
    <div className={styles.kzInputWrapper} title={schemaTooltip}>
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        className={`${isReadonly ? styles.kzInputReadonly : styles.kzInput} ${displayError ? styles.kzInputError : ''}`}
        value={field.computed ? formatCurrency(value) : localValue}
        onChange={e => handleChange(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={e => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        placeholder={isDecimal ? '0,00' : '0'}
        readOnly={isReadonly}
        disabled={isReadonly}
      />
      <span className={styles.kzBadge}>Kz{field.kz}</span>
      {displayError && (
        <span className={styles.kzFieldError}>{displayError}</span>
      )}
    </div>
  );
};

/** Section navigation sidebar */
const SectionSidebar: FC<{
  sections: UStVASection[];
  activeSection: number | null;
  kzData: Map<number, KzEntry>;
  onSectionClick: (num: number) => void;
  zeitraum: string;
}> = ({ sections, activeSection, kzData, onSectionClick, zeitraum }) => {
  const hasSectionValues = useCallback(
    (sectionNum: number) => {
      const section = sections.find(s => s.number === sectionNum);
      if (!section) return false;
      return section.groups.some(g =>
        g.fields.some(f => (kzData.get(f.kz)?.value ?? 0) !== 0)
      );
    },
    [kzData, sections]
  );

  return (
    <div className={styles.sidebarNav}>
      <div className={styles.sidebarHeader}>UStVA 2026</div>
      <div className={styles.sidebarSubheader}>
        {zeitraum || 'Kein Zeitraum'}
      </div>
      {sections.map(section => (
        <div
          key={section.number}
          className={`${styles.sidebarItem} ${activeSection === section.number ? styles.sidebarItemActive : ''}`}
          onClick={() => onSectionClick(section.number)}
        >
          <span className={styles.sidebarNumber}>{section.number}</span>
          <span style={{ flex: 1 }}>{section.title}</span>
          {hasSectionValues(section.number) && (
            <span className={styles.sidebarCheckmark}>&#10003;</span>
          )}
        </div>
      ))}
    </div>
  );
};

/** Dashboard with period cards */
const PeriodDashboard: FC<{
  rows: string[];
  currentIndex: number | null;
  onSelect: (index: number) => void;
}> = ({ rows, currentIndex, onSelect }) => (
  <div className={styles.dashboard}>
    <div className={styles.dashboardTitle}>Meldezeiträume</div>
    <div className={styles.dashboardGrid}>
      {rows.map((title, i) => (
        <div
          key={i}
          className={`${styles.periodCard} ${currentIndex === i ? styles.periodCardActive : ''}`}
          onClick={() => onSelect(i)}
        >
          <div className={styles.periodCardTitle}>
            {title || `Zeile ${i + 1}`}
          </div>
          <div className={styles.periodCardMeta}>
            <span className={styles.statusDraft}>Entwurf</span>
          </div>
        </div>
      ))}
      <div className={styles.addPeriodButton}>+ Neuen Zeitraum anlegen</div>
    </div>
  </div>
);

// =============================================================================
// History (sl-wlcm/sl-jwvv: Prisma-backed sealed filings)
// =============================================================================

interface SealedFiling {
  id: string;
  zeitraum: string;
  transferticket: string;
  status: string;
  schemaVersion: string;
  isCorrection: boolean;
  filedAt: string;
  filedByUserId: string;
}

interface SealedFilingDetail extends SealedFiling {
  kzSnapshot: Record<string, number | boolean>;
  payloadHash: string | null;
  errorMessage: string | null;
}

interface UstvaSchemaSnapshot {
  fields: Record<string, { label: string }>;
}

/** Resolve human label from the exact filing schema revision */
function resolveKzLabel(
  kzKey: string,
  schema: UstvaSchemaSnapshot | null
): string {
  const normalized = kzKey.startsWith('Kz')
    ? kzKey
    : `Kz${kzKey.replace(/^\D+/, '')}`;
  return schema?.fields[normalized]?.label ?? `[UNMAPPED: ${kzKey}]`;
}

/** Fetch sealed filings from Prisma-backed REST API */
function useElsterFilings(
  workspaceId: string,
  refreshNonce = 0
): SealedFiling[] {
  const [filings, setFilings] = useState<SealedFiling[]>([]);

  useEffect(() => {
    let cancelled = false;
    workspaceApiFetch(
      `/api/workspace/${workspaceId}/elster/filings`,
      {
        credentials: 'include',
      },
      elsterPolicy(workspaceId, 'filings')
    )
      .then(res => (res.ok ? res.json() : []))
      .then(data => {
        if (!cancelled) setFilings(data as SealedFiling[]);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [workspaceId, refreshNonce]);

  return filings;
}

/** Format Kz snapshot values for display */
function formatSnapshotValue(value: number | boolean): string {
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Compute diff between two Kz snapshots */
function computeKzDiff(
  current: Record<string, number | boolean>,
  previous: Record<string, number | boolean>
): Array<{ kz: string; oldValue: string; newValue: string }> {
  const diffs: Array<{ kz: string; oldValue: string; newValue: string }> = [];
  const allKeys = new Set([...Object.keys(current), ...Object.keys(previous)]);

  for (const kz of allKeys) {
    const oldVal = previous[kz];
    const newVal = current[kz];
    if (oldVal !== newVal) {
      diffs.push({
        kz,
        oldValue: oldVal !== undefined ? formatSnapshotValue(oldVal) : '\u2014',
        newValue: newVal !== undefined ? formatSnapshotValue(newVal) : '\u2014',
      });
    }
  }

  return diffs.sort((a, b) =>
    a.kz.localeCompare(b.kz, undefined, { numeric: true })
  );
}

const HistorySection: FC<{
  workspaceId: string;
  focusFilingId?: string | null;
  refreshNonce?: number;
}> = ({ workspaceId, focusFilingId = null, refreshNonce = 0 }) => {
  const filings = useElsterFilings(workspaceId, refreshNonce);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedDetail, setExpandedDetail] =
    useState<SealedFilingDetail | null>(null);
  const [previousSnapshot, setPreviousSnapshot] = useState<Record<
    string,
    number | boolean
  > | null>(null);
  const [schemaByVersion, setSchemaByVersion] = useState<
    Record<string, UstvaSchemaSnapshot>
  >({});

  // Fetch detail when expanding
  useEffect(() => {
    if (!expandedId) {
      setExpandedDetail(null);
      setPreviousSnapshot(null);
      return;
    }

    let cancelled = false;
    workspaceApiFetch(
      `/api/workspace/${workspaceId}/elster/filings/${expandedId}`,
      {
        credentials: 'include',
      },
      elsterPolicy(workspaceId, `filing:${expandedId}`)
    )
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (!cancelled && data) {
          setExpandedDetail(data as SealedFilingDetail);

          // Find previous filing for same zeitraum (for diff)
          const currentFiling = filings.find(f => f.id === expandedId);
          if (currentFiling) {
            const sameZeitraum = filings.filter(
              f => f.zeitraum === currentFiling.zeitraum && f.id !== expandedId
            );
            // Find the most recent one that's older than current
            const older = sameZeitraum.find(
              f => new Date(f.filedAt) < new Date(currentFiling.filedAt)
            );
            if (older) {
              workspaceApiFetch(
                `/api/workspace/${workspaceId}/elster/filings/${older.id}`,
                {
                  credentials: 'include',
                },
                elsterPolicy(workspaceId, `filing:${older.id}`)
              )
                .then(res => (res.ok ? res.json() : null))
                .then(prevData => {
                  if (!cancelled && prevData) {
                    setPreviousSnapshot(
                      (prevData as SealedFilingDetail).kzSnapshot
                    );
                  }
                })
                .catch(() => {});
            }
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [expandedId, workspaceId, filings]);

  // Resolve schema labels by immutable schema version from filing snapshot
  useEffect(() => {
    if (!focusFilingId) return;
    if (!filings.some(f => f.id === focusFilingId)) return;
    setExpandedId(focusFilingId);
  }, [focusFilingId, filings]);

  useEffect(() => {
    const neededVersions = new Set<string>();
    for (const filing of filings) {
      if (filing.schemaVersion && !schemaByVersion[filing.schemaVersion]) {
        neededVersions.add(filing.schemaVersion);
      }
    }
    if (neededVersions.size === 0) return;

    let cancelled = false;
    for (const version of neededVersions) {
      workspaceApiFetch(
        `/api/workspace/${workspaceId}/elster/schema/by-version/${encodeURIComponent(version)}`,
        {
          credentials: 'include',
        },
        elsterPolicy(workspaceId, `schema:${version}`)
      )
        .then(res => (res.ok ? res.json() : null))
        .then(data => {
          if (cancelled || !data) return;
          setSchemaByVersion(prev => ({
            ...prev,
            [version]: data as UstvaSchemaSnapshot,
          }));
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [filings, schemaByVersion, workspaceId]);

  if (filings.length === 0) return null;

  // Group by zeitraum for correction chain display
  const grouped = new Map<string, SealedFiling[]>();
  for (const filing of filings) {
    const group = grouped.get(filing.zeitraum) ?? [];
    group.push(filing);
    grouped.set(filing.zeitraum, group);
  }

  return (
    <div className={styles.historySection} id="elster-history-section">
      <div className={styles.historySectionTitle}>{'\u00DC'}bermittlungen</div>
      <table className={styles.historyTable}>
        <thead>
          <tr>
            <th className={styles.historyTh} style={{ width: '24px' }} />
            <th className={styles.historyTh}>Zeitraum</th>
            <th className={styles.historyTh}>Status</th>
            <th className={styles.historyTh}>Transferticket</th>
            <th className={styles.historyTh}>Gesendet am</th>
          </tr>
        </thead>
        <tbody>
          {filings.map(filing => {
            const isExpanded = expandedId === filing.id;
            const groupSize = grouped.get(filing.zeitraum)?.length ?? 1;
            const kzDiff =
              isExpanded && expandedDetail && previousSnapshot
                ? computeKzDiff(expandedDetail.kzSnapshot, previousSnapshot)
                : [];
            const filingSchema = schemaByVersion[filing.schemaVersion] ?? null;

            return (
              <Fragment key={filing.id}>
                <tr
                  className={`${styles.historyRow} ${isExpanded ? styles.historyRowExpanded : ''}`}
                  onClick={() => setExpandedId(isExpanded ? null : filing.id)}
                >
                  <td className={styles.historyTd}>
                    <span
                      className={`${styles.historyExpandIcon} ${isExpanded ? styles.historyExpandIconOpen : ''}`}
                    >
                      {'\u25B8'}
                    </span>
                  </td>
                  <td className={styles.historyTd}>{filing.zeitraum}</td>
                  <td className={styles.historyTd}>
                    {filing.isCorrection ? (
                      <span className={styles.historyCorrectionBadge}>
                        {'\u21BB'} Berichtigung
                      </span>
                    ) : (
                      filing.status
                    )}
                    {groupSize > 1 && !filing.isCorrection && (
                      <span
                        style={{
                          color: 'var(--streamlain-v2-text-secondary)',
                          fontSize: '11px',
                          marginLeft: '4px',
                        }}
                      >
                        ({groupSize}x)
                      </span>
                    )}
                  </td>
                  <td className={styles.historyTd}>
                    <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                      {filing.transferticket}
                    </span>
                  </td>
                  <td className={styles.historyTd}>
                    {new Date(filing.filedAt).toLocaleDateString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                </tr>

                {isExpanded && expandedDetail && (
                  <tr className={styles.historyDetailRow}>
                    <td colSpan={5} className={styles.historyDetailCell}>
                      {/* Correction diff */}
                      {kzDiff.length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: '12px',
                              marginBottom: '6px',
                              color: 'var(--streamlain-v2-text-primary)',
                            }}
                          >
                            {'\u0394'} Änderungen zur Voranmeldung
                          </div>
                          <div className={styles.historyDetailGrid}>
                            {kzDiff.map(diff => (
                              <div
                                key={diff.kz}
                                className={styles.historyDetailItem}
                              >
                                <span
                                  className={styles.historyDetailLabel}
                                  title={resolveKzLabel(diff.kz, filingSchema)}
                                >
                                  {diff.kz} ·{' '}
                                  {resolveKzLabel(diff.kz, filingSchema)}
                                </span>
                                <span className={styles.historyDetailValue}>
                                  <span
                                    style={{
                                      textDecoration: 'line-through',
                                      color:
                                        'var(--streamlain-v2-text-secondary)',
                                    }}
                                  >
                                    {diff.oldValue}
                                  </span>
                                  {' \u2192 '}
                                  {diff.newValue}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Full Kz snapshot */}
                      {Object.keys(expandedDetail.kzSnapshot).length > 0 ? (
                        <div className={styles.historyDetailGrid}>
                          {Object.entries(expandedDetail.kzSnapshot)
                            .sort(([a], [b]) =>
                              a.localeCompare(b, undefined, { numeric: true })
                            )
                            .map(([kz, value]) => (
                              <div
                                key={kz}
                                className={styles.historyDetailItem}
                              >
                                <span
                                  className={styles.historyDetailLabel}
                                  title={resolveKzLabel(kz, filingSchema)}
                                >
                                  {kz} · {resolveKzLabel(kz, filingSchema)}
                                </span>
                                <span className={styles.historyDetailValue}>
                                  {formatSnapshotValue(value)}
                                </span>
                              </div>
                            ))}
                        </div>
                      ) : (
                        <div
                          style={{
                            color: 'var(--streamlain-v2-text-secondary)',
                            fontSize: '12px',
                          }}
                        >
                          Kein Kz-Snapshot verf{'\u00FC'}gbar.
                        </div>
                      )}
                      <div className={styles.historyMetaRow}>
                        <span>Schema: {expandedDetail.schemaVersion}</span>
                        {expandedDetail.payloadHash && (
                          <span
                            title={expandedDetail.payloadHash}
                            style={{ cursor: 'help' }}
                          >
                            Hash: {expandedDetail.payloadHash.slice(0, 12)}
                            {'\u2026'}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// =============================================================================
// Confirm Dialog (enhanced with summary)
// =============================================================================

const ConfirmDialog: FC<{
  zeitraum: string;
  zahllast: number;
  ustGesamt: number;
  vstGesamt: number;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ zeitraum, zahllast, ustGesamt, vstGesamt, onConfirm, onCancel }) => {
  const [confirmed, setConfirmed] = useState(false);

  return (
    <div className={styles.confirmOverlay} onClick={onCancel}>
      <div className={styles.confirmDialog} onClick={e => e.stopPropagation()}>
        <div className={styles.confirmTitle}>UStVA an ELSTER senden?</div>

        <div className={styles.confirmSummary}>
          <div className={styles.confirmSummaryRow}>
            <span>Zeitraum</span>
            <span style={{ fontWeight: 600 }}>{zeitraum}</span>
          </div>
          <div className={styles.confirmSummaryRow}>
            <span>Umsatzsteuer</span>
            <span>{formatCurrency(ustGesamt)} \u20AC</span>
          </div>
          <div className={styles.confirmSummaryRow}>
            <span>Vorsteuer</span>
            <span>{formatCurrency(vstGesamt)} \u20AC</span>
          </div>
          <div
            className={styles.confirmSummaryRow}
            style={{
              fontWeight: 700,
              borderTop:
                '1px solid var(--streamlain-v2-layer-insideBorder-border)',
              paddingTop: '6px',
            }}
          >
            <span>{zahllast >= 0 ? 'Zahllast' : 'Erstattung'}</span>
            <span style={{ color: zahllast >= 0 ? '#C62828' : '#137333' }}>
              {formatCurrency(Math.abs(zahllast))} \u20AC
            </span>
          </div>
        </div>

        <div className={styles.confirmBody}>
          Die Umsatzsteuervoranmeldung wird verbindlich an das Finanzamt
          \u00FCbermittelt. Dieser Vorgang kann nicht r\u00FCckg\u00E4ngig
          gemacht werden.
        </div>

        <div className={styles.confirmCheckboxRow}>
          <input
            type="checkbox"
            checked={confirmed}
            onChange={e => setConfirmed(e.target.checked)}
            className={styles.kzCheckbox}
          />
          <span>Ich best\u00E4tige die Richtigkeit der Angaben</span>
        </div>

        <div className={styles.confirmActions}>
          <Button variant="secondary" onClick={onCancel}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={onConfirm} disabled={!confirmed}>
            An ELSTER senden
          </Button>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const ElsterFullscreenView: FC<FullscreenViewProps> = ({
  blockId,
  onClose,
}) => {
  const registry = useService(FullscreenRegistry);
  const workspaceId = useService(WorkspaceService).workspace.id;
  const elsterBlocks = useElsterBlocks();
  const ericaConfig = useEricaConfig();

  // Current block = the one matching blockId (or first tab)
  const currentBlock = useMemo(
    () => elsterBlocks.find(b => b.id === blockId) ?? elsterBlocks[0] ?? null,
    [elsterBlocks, blockId]
  );

  // Database sources
  const inputDataSource = useDatabaseDataSource(
    currentBlock?.inputTableId ?? null
  );
  const inputRows = useDatabaseRows(inputDataSource);
  const kzData = useKzData(
    inputDataSource,
    currentBlock?.selectedRowIndex ?? null
  );

  // Extract plain values for Zahllast calculation
  const kzValues = useMemo(() => {
    const map = new Map<number, number>();
    for (const [kz, data] of kzData) {
      map.set(kz, data.value);
    }
    return map;
  }, [kzData]);

  const zahllast = useMemo(() => calculateZahllast(kzValues), [kzValues]);
  const get = useCallback((kz: number) => kzValues.get(kz) ?? 0, [kzValues]);

  // sl-omtr/sl-yphr.31.5: Schema-driven sections, validation + tooltips
  const ustvaSchema = useUstvaSchema(workspaceId);
  const ustvaSections = useUstvaSections(ustvaSchema);
  const validateKzField = useKzFieldValidation(ustvaSchema);
  const kzTooltips = useKzTooltips(ustvaSchema);

  // Simplified totals for confirm dialog
  const ust19 = Math.round(get(81) * 0.19 * 100) / 100;
  const ust7 = Math.round(get(86) * 0.07 * 100) / 100;
  const ustGesamt =
    ust19 +
    ust7 +
    get(36) +
    get(43) +
    get(44) +
    get(47) +
    get(93) +
    get(21) +
    get(80) +
    get(85) +
    get(96) +
    get(98);
  const vstGesamt =
    get(66) + get(61) + get(62) + get(67) + get(63) - get(64) + get(59);

  // ── Intersection Observer for sidebar ──
  const sectionRefs = useRef(new Map<number, HTMLElement | null>());
  const activeSection = useActiveSectionObserver(sectionRefs);

  const handleSectionClick = useCallback((sectionNum: number) => {
    const el = sectionRefs.current.get(sectionNum);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  // ── sl-yphr.29: Schema Migration ──
  const editorService = useServiceOptional(EditorService);
  const editorForMigration = editorService?.editor;
  const editorContainerForMigration = useLiveData(
    editorForMigration?.editorContainer$ ?? null
  );

  const migration = useElsterSchemaMigration(
    workspaceId,
    currentBlock?.schemaVersion ?? null,
    inputDataSource
  );

  const handleMigrationConfirm = useCallback(() => {
    if (
      !migration.diff ||
      !inputDataSource ||
      !currentBlock ||
      !editorContainerForMigration?.doc
    )
      return;

    const blockModel = editorContainerForMigration.doc.getBlock(
      currentBlock.id
    );
    if (!blockModel?.model) return;

    executeSchemaMigration(
      inputDataSource,
      migration.diff,
      (version: string) => {
        blockModel.model.store.transact(() => {
          (blockModel.model.props as Record<string, unknown>).schemaVersion =
            version;
        });
      }
    );

    migration.markComplete();
  }, [migration, inputDataSource, currentBlock, editorContainerForMigration]);

  // ── Validate / Submit state ──
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    errors: string[];
  } | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitResult, setSubmitResult] = useState<{
    status: string;
    id?: string;
    transferticket?: string;
    errorMessage?: string;
  } | null>(null);
  const [historyFocusId, setHistoryFocusId] = useState<string | null>(null);
  const [historyRefreshNonce, setHistoryRefreshNonce] = useState(0);

  // Build Kennzahlen payload from current kzData
  const buildKennzahlenPayload = useCallback(() => {
    const kennzahlen: Array<{ kz: string; betrag: number }> = [];
    for (const [kz, data] of kzData) {
      if (data.value !== 0) {
        kennzahlen.push({ kz: String(kz), betrag: data.value });
      }
    }
    return kennzahlen;
  }, [kzData]);

  // Derive year + period
  const deriveYearAndPeriod = useCallback((): {
    year: number;
    period: string;
  } => {
    if (currentBlock?.selectedRowIndex == null) {
      return { year: new Date().getFullYear(), period: '01' };
    }
    const title = inputRows[currentBlock.selectedRowIndex] ?? '';
    const match = /(\d{1,2})\D+(\d{4})/.exec(title);
    if (match) {
      return {
        year: parseInt(match[2], 10),
        period: match[1].padStart(2, '0'),
      };
    }
    return { year: new Date().getFullYear(), period: '01' };
  }, [currentBlock, inputRows]);

  const currentZeitraum =
    currentBlock?.selectedRowIndex !== null
      ? inputRows[currentBlock.selectedRowIndex ?? -1] || ''
      : '';

  // ── Validate ──
  const handleValidate = useCallback(async () => {
    setIsValidating(true);
    setValidationResult(null);
    setSubmitResult(null);
    try {
      const { year, period } = deriveYearAndPeriod();
      const res = await workspaceApiFetch(
        `/api/workspace/${workspaceId}/erica/ustva/validate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            year,
            period,
            kennzahlen: buildKennzahlenPayload(),
          }),
        },
        elsterPolicy(workspaceId, 'validate', true)
      );
      if (res.ok) {
        setValidationResult(await res.json());
      } else {
        const text = await res.text().catch(() => 'Fehler');
        setValidationResult({ valid: false, errors: [text] });
      }
    } catch (err) {
      setValidationResult({
        valid: false,
        errors: [err instanceof Error ? err.message : 'Netzwerkfehler'],
      });
    } finally {
      setIsValidating(false);
    }
  }, [workspaceId, deriveYearAndPeriod, buildKennzahlenPayload]);

  const pollUntilDone = useCallback(
    async (
      filingId: string
    ): Promise<{
      status: string;
      transferticket?: string;
      errorMessage?: string;
    }> => {
      for (let i = 0; i < 25; i++) {
        const statusRes = await workspaceApiFetch(
          `/api/workspace/${workspaceId}/erica/filings/${filingId}`,
          { credentials: 'include' },
          elsterPolicy(workspaceId, `filing-status:${filingId}`)
        );
        if (!statusRes.ok) {
          return {
            status: 'failed',
            errorMessage: 'Statusabfrage fehlgeschlagen.',
          };
        }
        const statusData = (await statusRes.json()) as {
          status: string;
          transferticket?: string;
          errorMessage?: string;
        };
        if (statusData.status === 'success' || statusData.status === 'failed') {
          return statusData;
        }
        await new Promise(resolve => setTimeout(resolve, 1200));
      }
      return { status: 'processing' };
    },
    [workspaceId]
  );

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    setShowConfirm(false);
    setIsSubmitting(true);
    setSubmitResult(null);
    setHistoryFocusId(null);
    try {
      const { year, period } = deriveYearAndPeriod();
      const res = await workspaceApiFetch(
        `/api/workspace/${workspaceId}/erica/ustva`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            year,
            period,
            kennzahlen: buildKennzahlenPayload(),
            inputRowIndex: currentBlock?.selectedRowIndex ?? undefined,
            inputTableId: currentBlock?.inputTableId ?? undefined,
          }),
        },
        elsterPolicy(workspaceId, 'submit', true)
      );
      if (res.ok) {
        const data = (await res.json()) as { status: string; id?: string };
        setSubmitResult({ status: data.status, id: data.id });

        if (data.id) {
          const finalStatus = await pollUntilDone(data.id);
          if (finalStatus.status === 'success') {
            setSubmitResult({
              status: 'success',
              id: data.id,
              transferticket: finalStatus.transferticket,
            });
            setHistoryFocusId(data.id);
            setHistoryRefreshNonce(prev => prev + 1);
          } else if (finalStatus.status === 'failed') {
            const errorMessage =
              finalStatus.errorMessage ?? '\u00DCbermittlung fehlgeschlagen.';
            setSubmitResult({ status: 'failed', id: data.id, errorMessage });
            notify.error({
              title: 'ELSTER-\u00DCbermittlung fehlgeschlagen',
              message: errorMessage,
            });
          }
        }
      } else if (res.status === 429) {
        const data = (await res.json().catch(() => ({}))) as {
          message?: string;
        };
        const errorMessage =
          data.message ??
          'Zu viele Anfragen. Bitte sp\u00E4ter erneut versuchen.';
        setSubmitResult({
          status: 'rate_limited',
          errorMessage,
        });
        notify.error({
          title: 'Rate limit erreicht',
          message: errorMessage,
        });
      } else {
        const text = await res.text().catch(() => 'Fehler');
        setSubmitResult({ status: 'failed', errorMessage: text });
        notify.error({
          title: 'ELSTER-\u00DCbermittlung fehlgeschlagen',
          message: text,
        });
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Netzwerkfehler';
      setSubmitResult({
        status: 'failed',
        errorMessage,
      });
      notify.error({
        title: 'ELSTER-\u00DCbermittlung fehlgeschlagen',
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    workspaceId,
    deriveYearAndPeriod,
    buildKennzahlenPayload,
    currentBlock,
    pollUntilDone,
  ]);

  // Write cell value back to database
  const handleValueChange = useCallback(
    (kz: number, value: number) => {
      const entry = kzData.get(kz);
      if (!entry || !inputDataSource) return;
      inputDataSource.cellValueChange(entry.rowId, entry.propId, value);
    },
    [kzData, inputDataSource]
  );

  // Period selection (from dashboard card click)
  const handlePeriodSelect = useCallback(
    (index: number) => {
      if (!currentBlock || !editorContainerForMigration?.doc) return;
      const blockModel = editorContainerForMigration.doc.getBlock(
        currentBlock.id
      );
      if (!blockModel?.model) return;
      blockModel.model.store.transact(() => {
        (blockModel.model.props as Record<string, unknown>).selectedRowIndex =
          index;
      });
    },
    [currentBlock, editorContainerForMigration]
  );

  // Tab switch
  const handleTabClick = useCallback(
    (tabBlockId: string) => {
      registry.open('elster', tabBlockId);
    },
    [registry]
  );

  // ── Empty state ──
  if (elsterBlocks.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <span style={{ fontSize: '32px' }}>&#127963;</span>
          <span>Kein ELSTER-Block im aktuellen Dokument</span>
          <Button variant="secondary" onClick={onClose}>
            Schlie\u00DFen
          </Button>
        </div>
      </div>
    );
  }

  const hasSelectedPeriod =
    currentBlock?.selectedRowIndex !== null &&
    currentBlock?.selectedRowIndex !== undefined;

  return (
    <div className={styles.container}>
      {/* Confirm Dialog */}
      {showConfirm && (
        <ConfirmDialog
          zeitraum={currentZeitraum}
          zahllast={zahllast}
          ustGesamt={ustGesamt}
          vstGesamt={vstGesamt}
          onConfirm={() => void handleSubmit()}
          onCancel={() => setShowConfirm(false)}
        />
      )}

      {/* sl-yphr.29: Schema Migration Dialog */}
      {migration.needsMigration && !migration.dismissed && migration.diff && (
        <ElsterMigrationDialog
          diff={migration.diff}
          onConfirm={handleMigrationConfirm}
          onDismiss={migration.dismiss}
          blockingRows={migration.blockingRows}
        />
      )}

      {/* Tab Bar */}
      <div className={styles.tabBar}>
        {elsterBlocks.map(block => (
          <button
            key={block.id}
            className={`${styles.tab} ${block.id === currentBlock?.id ? styles.tabActive : ''}`}
            onClick={() => handleTabClick(block.id)}
          >
            {block.title}
          </button>
        ))}
      </div>

      {/* Migration Banner (after dismiss) */}
      {migration.needsMigration && migration.dismissed && migration.diff && (
        <ElsterMigrationBanner
          diff={migration.diff}
          onUpdate={handleMigrationConfirm}
        />
      )}

      {currentBlock && !hasSelectedPeriod ? (
        /* Dashboard: Period overview when no period selected */
        <PeriodDashboard
          rows={inputRows}
          currentIndex={currentBlock.selectedRowIndex}
          onSelect={handlePeriodSelect}
        />
      ) : currentBlock && hasSelectedPeriod ? (
        /* Form view: Sidebar + Content */
        <div className={styles.mainLayout}>
          {/* Sidebar Navigation */}
          <SectionSidebar
            sections={ustvaSections}
            activeSection={activeSection}
            kzData={kzData}
            onSectionClick={handleSectionClick}
            zeitraum={currentZeitraum}
          />

          {/* Content Area */}
          <div className={styles.contentArea}>
            <div className={styles.scrollArea}>
              {/* Config info row */}
              <div className={styles.configRow}>
                <div className={styles.configField}>
                  <span className={styles.configLabel}>Steuernummer</span>
                  <span className={styles.configValue}>
                    {ericaConfig?.steuernummer ?? '\u2014'}
                  </span>
                </div>
                <div className={styles.configField}>
                  <span className={styles.configLabel}>Bundesland</span>
                  <span className={styles.configValue}>
                    {ericaConfig?.bundesland ?? '\u2014'}
                  </span>
                </div>
                <div className={styles.configField}>
                  <span className={styles.configLabel}>Zeitraum</span>
                  <span className={styles.configValue}>
                    {currentZeitraum || '\u2014'}
                  </span>
                </div>
              </div>

              {/* Validation feedback */}
              {validationResult &&
                (validationResult.valid ? (
                  <div className={styles.validationSuccess}>
                    Validierung erfolgreich \u2014 Daten korrekt.
                  </div>
                ) : (
                  <div className={styles.validationErrorSummary}>
                    <div className={styles.validationErrorTitle}>
                      Validierungsfehler ({validationResult.errors.length})
                    </div>
                    {validationResult.errors.map((err, i) => (
                      <div key={i} className={styles.validationErrorItem}>
                        {err}
                      </div>
                    ))}
                  </div>
                ))}

              {submitResult && (
                <div
                  className={
                    submitResult.status === 'failed' ||
                    submitResult.status === 'rate_limited'
                      ? styles.validationError
                      : styles.validationSuccess
                  }
                >
                  {submitResult.status === 'processing' && (
                    <>UStVA \u00FCbermittelt. Filing-ID: {submitResult.id}</>
                  )}
                  {submitResult.status === 'success' && (
                    <>
                      UStVA erfolgreich verarbeitet. Filing-ID:{' '}
                      {submitResult.id}
                      {submitResult.transferticket && (
                        <>
                          {' '}
                          · Ticket: {submitResult.transferticket}{' '}
                          <a href="#elster-history-section">
                            In Historie anzeigen
                          </a>
                        </>
                      )}
                    </>
                  )}
                  {submitResult.status === 'failed' && (
                    <>
                      \u00DCbermittlung fehlgeschlagen:{' '}
                      {submitResult.errorMessage}
                    </>
                  )}
                  {submitResult.status === 'rate_limited' && (
                    <>{submitResult.errorMessage}</>
                  )}
                </div>
              )}

              {/* Form sections */}
              {ustvaSections.map(section => (
                <div
                  key={section.number}
                  className={styles.sectionBlock}
                  data-section={section.number}
                  ref={el => {
                    sectionRefs.current.set(section.number, el);
                  }}
                >
                  {/* Section header */}
                  <div className={styles.sectionHeader}>
                    <span className={styles.sectionNumber}>
                      {section.number}
                    </span>
                    <span className={styles.sectionTitle}>{section.title}</span>
                    {section.tooltip && (
                      <span
                        className={styles.sectionTooltipIcon}
                        title={section.tooltip}
                      >
                        ?
                      </span>
                    )}
                  </div>

                  {/* Field groups */}
                  {section.groups.map((group, gi) => (
                    <div key={gi} className={styles.fieldGroup}>
                      {group.title && (
                        <div className={styles.fieldGroupTitle}>
                          {group.title}
                        </div>
                      )}

                      {/* Column headers */}
                      {group.twoColumns ? (
                        <div className={styles.fieldGroupColumnHeaders}>
                          <span />
                          <span />
                          <span>Bemessungsgrundlage (\u20AC)</span>
                          <span>Steuer (\u20AC, Ct)</span>
                        </div>
                      ) : (
                        <div className={styles.fieldGroupColumnHeadersSingle}>
                          <span />
                          <span />
                          <span>Betrag (\u20AC)</span>
                        </div>
                      )}

                      {/* Fields */}
                      {group.fields.map(field => {
                        const entry = kzData.get(field.kz);
                        const value = field.computed
                          ? zahllast
                          : (entry?.value ?? 0);
                        const tooltip = kzTooltips.get(field.kz);

                        if (group.twoColumns) {
                          return (
                            <div key={field.kz} className={styles.kzRow}>
                              <span className={styles.kzZeile}>
                                {field.zeile ?? ''}
                              </span>
                              <span className={styles.kzLabel} title={tooltip}>
                                {field.label}
                                {tooltip && (
                                  <span
                                    className={styles.tooltipIcon}
                                    title={tooltip}
                                  >
                                    ?
                                  </span>
                                )}
                              </span>
                              {field.column === 'bmg' && (
                                <>
                                  <KzFieldInput
                                    field={field}
                                    value={value}
                                    onChange={v =>
                                      handleValueChange(field.kz, v)
                                    }
                                    schemaTooltip={tooltip}
                                    validateFn={validateKzField}
                                  />
                                  <span />
                                </>
                              )}
                              {field.column === 'steuer' && (
                                <>
                                  <span />
                                  <KzFieldInput
                                    field={field}
                                    value={value}
                                    onChange={v =>
                                      handleValueChange(field.kz, v)
                                    }
                                    schemaTooltip={tooltip}
                                    validateFn={validateKzField}
                                  />
                                </>
                              )}
                              {field.column === 'full' && (
                                <KzFieldInput
                                  field={field}
                                  value={value}
                                  onChange={v => handleValueChange(field.kz, v)}
                                  schemaTooltip={tooltip}
                                  validateFn={validateKzField}
                                />
                              )}
                            </div>
                          );
                        }

                        return (
                          <div key={field.kz} className={styles.kzRowSingle}>
                            <span className={styles.kzZeile}>
                              {field.zeile ?? ''}
                            </span>
                            <span className={styles.kzLabel} title={tooltip}>
                              {field.label}
                              {tooltip && (
                                <span
                                  className={styles.tooltipIcon}
                                  title={tooltip}
                                >
                                  ?
                                </span>
                              )}
                            </span>
                            <KzFieldInput
                              field={field}
                              value={value}
                              onChange={v => handleValueChange(field.kz, v)}
                              schemaTooltip={tooltip}
                              validateFn={validateKzField}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              ))}

              {/* Summary section */}
              <div className={styles.summarySection}>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>
                    Umsatzsteuer gesamt
                  </span>
                  <span className={styles.summaryValue}>
                    {formatCurrency(ustGesamt)} \u20AC
                  </span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>Vorsteuer gesamt</span>
                  <span className={styles.summaryValue}>
                    {formatCurrency(vstGesamt)} \u20AC
                  </span>
                </div>
                <div className={styles.summaryTotal}>
                  <span>{zahllast >= 0 ? 'Zahllast' : 'Erstattung'}</span>
                  <span
                    style={{ color: zahllast >= 0 ? '#C62828' : '#137333' }}
                  >
                    {formatCurrency(Math.abs(zahllast))} \u20AC
                  </span>
                </div>
              </div>

              {/* History */}
              <HistorySection
                workspaceId={workspaceId}
                focusFilingId={historyFocusId}
                refreshNonce={historyRefreshNonce}
              />
            </div>

            {/* Sticky Action Bar */}
            {
              <div className={styles.actionBar}>
                <div className={styles.actionBarLeft}>
                  <div className={styles.actionBarZahllast}>
                    <span
                      style={{
                        color: 'var(--streamlain-v2-text-secondary)',
                        fontWeight: 400,
                      }}
                    >
                      {zahllast >= 0 ? 'Zahllast' : 'Erstattung'}:
                    </span>
                    <span
                      style={{ color: zahllast >= 0 ? '#C62828' : '#137333' }}
                    >
                      {formatCurrency(Math.abs(zahllast))} \u20AC
                    </span>
                  </div>
                </div>
                <div className={styles.actionBarRight}>
                  <Button
                    variant="secondary"
                    disabled={isValidating || isSubmitting}
                    onClick={() => void handleValidate()}
                  >
                    {isValidating ? 'Validiere...' : 'Validieren'}
                  </Button>
                  <Button
                    variant="primary"
                    disabled={isValidating || isSubmitting}
                    onClick={() => setShowConfirm(true)}
                  >
                    {isSubmitting ? 'Sende...' : 'An ELSTER senden'}
                  </Button>
                </div>
              </div>
            }
          </div>
        </div>
      ) : null}
    </div>
  );
};
